import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { CostReportsCacheService } from '../services/cost-reports-cache.service';
import { CostAnalysisStateService } from '../services/cost-analysis-state.service';
import { buildSearchCommand } from '../utils/filter-command.util';
import { FabricProcessCellRowDto, ProcessSummaryRowDto, CostReportsProxyService } from '../../../proxies/cost-reports.proxy.service';
import {
  CostSourceFilter,
  HierarchyContext,
  HierarchyLevel,
  PROCESS_LABELS,
  ProcessDetailContext,
  ProcessKey,
  ProcessKeys,
} from '../models/cost-analysis.models';

interface ProcessSummaryDisplayRow extends ProcessSummaryRowDto {
  cumulativeCostPerLbUsd: number;
}

const KNITTING_WORK_AREA_LABELS: Record<string, { key: string; fallback: string }> = {
  'NT.WA-KNITTING-1': {
    key: 'Cost_Analysis_Knitting_Area_NT',
    fallback: 'NORTHERN TEXTILES PRODUCTION',
  },
  'GUAK.WA-KNITTING-3': {
    key: 'Cost_Analysis_Knitting_Area_GUAK',
    fallback: 'KNITTING IMPORTED GREIGE',
  },
};

@Component({
  selector: 'app-process-summary',
  templateUrl: './process-summary.component.html',
  styleUrl: './process-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProcessSummaryComponent {
  private readonly cache = inject(CostReportsCacheService);
  private readonly proxy = inject(CostReportsProxyService);
  private readonly state = inject(CostAnalysisStateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly year = input.required<number>();
  readonly month = input.required<number>();
  readonly costSource = input.required<CostSourceFilter>();
  readonly productionLbs = input<number>(0);

  readonly processClick = output<ProcessDetailContext>();

  readonly report = this.state.byProcessReport;
  readonly loading = signal(false);
  readonly expandedProcesses = signal<ReadonlySet<ProcessKey>>(new Set<ProcessKey>());

  readonly processes: readonly ProcessKey[] = [
    ProcessKeys.RawMaterial,
    ProcessKeys.Knitting,
    ProcessKeys.Greige,
    ProcessKeys.Batching,
    ProcessKeys.Dyeing,
    ProcessKeys.Finishing,
    ProcessKeys.Rolling,
  ];
  readonly processLabels = PROCESS_LABELS;

  readonly processSummary = computed<ProcessSummaryDisplayRow[]>(() => {
    const rep = this.report();
    if (!rep) return [];
    let running = 0;
    return rep.processRows.map((row) => {
      running += row.realCostPerLbUsd;
      return { ...row, cumulativeCostPerLbUsd: running };
    });
  });

  readonly fabricRows = computed<(FabricProcessCellRowDto & { totalCostPerLbUsd: number })[]>(() => {
    const rep = this.report();
    if (!rep) return [];
    return [...rep.fabricMatrix]
      .map((f) => ({
        ...f,
        totalCostPerLbUsd: this.processes.reduce((sum, p) => sum + (f.byProcess[p]?.costPerLbUsd ?? 0), 0),
      }))
      .sort((a, b) => b.totalCostPerLbUsd - a.totalCostPerLbUsd);
  });

  readonly matrixTotals = computed(() => {
    const rep = this.report();
    if (!rep) {
      return {
        totalLbs: 0,
        totalCostUsd: 0,
        costPerLbUsd: 0,
        sumOfProcessCostPerLb: 0,
        processCostPerLb: {} as Record<ProcessKey, number>,
      };
    }
    return rep.matrixTotals;
  });

  private readonly loadTrigger$ = new Subject<void>();

  constructor() {
    this.loadTrigger$
      .pipe(
        switchMap(() => {
          const y = this.year();
          const m = this.month();
          const src = this.costSource();
          const filters = this.state.filters();
          if (!y || !m) return of(null);
          const cmd = buildSearchCommand(src, filters);
          this.loading.set(true);
          const obs = cmd
            ? this.proxy.searchProcessSummaryReport(y, m, cmd, filters.onlyCompletedRolls)
            : this.cache.getProcessSummary(y, m, src);
          return obs.pipe(
            catchError(() => of(null)),
            finalize(() => this.loading.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resp) => {
        this.state.byProcessReport.set(resp && resp.success && resp.data ? resp.data : null);
      });

    // Deliberately does NOT track year()/month()/costSource(): those only
    // take effect when the page's Apply/Reset bumps reloadRequested (see
    // CostAnalysisStateService), so picking a period or cost source alone
    // never fires a search on its own. state.filters() (the "narrow the
    // report" pickers) stay live/instant, matching existing UX.
    effect(() => {
      this.state.filters();
      this.state.reloadRequested();
      this.loadTrigger$.next();
    });
  }

  // Raw Material is not a real process (yarn cost booked into other areas), so
  // its Lbs / Variance columns aren't meaningful — render them as "—" always.
  isRawMaterialRow(row: ProcessSummaryRowDto): boolean {
    return row.process === ProcessKeys.RawMaterial;
  }

  hasSubRows(row: ProcessSummaryRowDto): boolean {
    return (row.subRows?.length ?? 0) > 0;
  }

  subRowLabelKey(row: ProcessSummaryRowDto): string {
    return KNITTING_WORK_AREA_LABELS[row.locationCode ?? '']?.key ?? row.locationCode ?? row.label;
  }

  subRowLabelFallback(row: ProcessSummaryRowDto): string {
    return KNITTING_WORK_AREA_LABELS[row.locationCode ?? '']?.fallback ?? row.locationCode ?? row.label;
  }

  isExpanded(row: ProcessSummaryRowDto): boolean {
    return this.expandedProcesses().has(row.process);
  }

  toggleSubRows(row: ProcessSummaryRowDto): void {
    const next = new Set(this.expandedProcesses());
    if (next.has(row.process)) {
      next.delete(row.process);
    } else {
      next.add(row.process);
    }
    this.expandedProcesses.set(next);
  }

  rowTotalCostPerLb(fabric: FabricProcessCellRowDto): number {
    return this.processes.reduce((sum, p) => sum + (fabric.byProcess[p]?.costPerLbUsd ?? 0), 0);
  }

  onProcessClick(fabric: FabricProcessCellRowDto, process: ProcessKey): void {
    const ctx: HierarchyContext = {
      fabricCode: fabric.context.fabricCode ?? undefined,
      fabricDescription: fabric.context.fabricDescription ?? undefined,
    };
    this.processClick.emit({
      process,
      level: 'fabric' as HierarchyLevel,
      context: ctx,
      year: this.year(),
      month: this.month(),
    });
  }
}
